import { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import SellerLayout from "@/components/SellerLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Wallet, TrendingUp, TrendingDown, Plus, Download, CreditCard, Smartphone, Loader2, ExternalLink, FileDown } from "lucide-react";
import { formatToIST } from "@/utils/dateUtils";
import { toast } from "sonner";

// Temporarily hide the "Recharge via UPI" option in the seller wallet. The UPI
// flow + handlers are left intact — flip this to true to restore it.
const SHOW_UPI_RECHARGE = false;

const SellerWalletPage = ({ user, onLogout }) => {
  const [balance, setBalance] = useState(0);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, pages: 0 });
  
  // Recharge dialog states
  const [showRechargeDialog, setShowRechargeDialog] = useState(false);
  const [rechargeAmount, setRechargeAmount] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState(null); // null | "upi" | "razorpay"
  const [upiTxnId, setUpiTxnId] = useState(null);
  const [checkingUpiStatus, setCheckingUpiStatus] = useState(false);
  const [downloadingReceipt, setDownloadingReceipt] = useState(null);

  useEffect(() => {
    fetchWalletData();
  }, [currentPage]);

  const fetchWalletData = async () => {
    try {
      setLoading(true);
      const [balanceRes, transactionsRes] = await Promise.all([
        axios.get(`${API}/wallet/balance`),
        axios.get(`${API}/wallet/transactions?page=${currentPage}&limit=50`)
      ]);
      
      setBalance(balanceRes.data.balance);
      setTransactions(transactionsRes.data.transactions);
      setPagination(transactionsRes.data.pagination);
    } catch (error) {
      console.error("Failed to fetch wallet data");
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    try {
      // Fetch all transactions for export (up to 10000)
      const response = await axios.get(`${API}/wallet/transactions?page=1&limit=10000`);
      const allTransactions = response.data.transactions || [];
      
      if (allTransactions.length === 0) {
        toast.error("No transactions to export");
        return;
      }
      
      // Create CSV content
      const headers = ["Date", "Type", "Amount", "Recharge Via", "Transaction ID", "Description", "Balance After", "Recharged By"];
      const csvRows = [headers.join(",")];
      
      allTransactions.forEach(txn => {
        const row = [
          `"${formatToIST(txn.created_at)}"`,
          txn.type === 'credit' ? 'Credit' : 'Debit',
          txn.type === 'credit' ? `+${txn.amount}` : `-${txn.amount}`,
          `"${txn.recharge_via || '-'}"`,
          `"${txn.transaction_id || txn.razorpay_payment_id || '-'}"`,
          `"${(txn.description || '').replace(/"/g, '""')}"`,
          txn.balance_after || '',
          `"${txn.recharged_by || 'System'}"`
        ];
        csvRows.push(row.join(","));
      });
      
      const csvContent = csvRows.join("\n");
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `my_wallet_transactions_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success("Transaction history exported successfully");
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Failed to export transaction history");
    }
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve) => {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handleRechargeRazorpay = async () => {
    const amount = parseFloat(rechargeAmount);
    if (!amount || amount < 100) return;

    setIsProcessing(true);
    try {
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway");
        setIsProcessing(false);
        return;
      }

      const orderResponse = await axios.post(`${API}/wallet/create-recharge-order`, { amount });
      const { order_id, amount: totalAmountInPaise, currency, key_id, wallet_amount, gateway_fee, total_amount } = orderResponse.data;

      const options = {
        key: key_id,
        amount: totalAmountInPaise,
        currency: currency,
        name: "EdgeLMS",
        description: `Wallet Recharge - ₹${wallet_amount}`,
        order_id: order_id,
        handler: async function (response) {
          try {
            const verifyResponse = await axios.post(`${API}/wallet/verify-payment`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              amount: wallet_amount
            });
            if (verifyResponse.data.success) {
              toast.success(`Wallet recharged successfully! ₹${wallet_amount} added.`);
              closeRechargeDialog();
              fetchWalletData();
            }
          } catch (error) {
            toast.error(error.response?.data?.detail || "Payment verification failed");
          } finally {
            setIsProcessing(false);
          }
        },
        prefill: {
          name: user.name || "",
          email: user.email || "",
          contact: user.phone || ""
        },
        theme: { color: "#00A699" },
        modal: {
          ondismiss: function() {
            setIsProcessing(false);
            toast.info("Payment cancelled");
          }
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to initiate recharge");
      setIsProcessing(false);
    }
  };

  const handleRechargeUPI = async () => {
    const amount = parseFloat(rechargeAmount);
    if (!amount || amount < 100) return;

    setIsProcessing(true);
    try {
      const response = await axios.post(`${API}/wallet/upi/create-order`, { amount });
      const { client_txn_id, payment_url } = response.data;

      setUpiTxnId(client_txn_id);
      // Open payment page in new tab
      window.open(payment_url, '_blank');
      setIsProcessing(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create UPI payment");
      setIsProcessing(false);
    }
  };

  const handleCheckUpiStatus = async () => {
    if (!upiTxnId) return;

    setCheckingUpiStatus(true);
    try {
      const response = await axios.post(`${API}/wallet/upi/check-status`, {
        client_txn_id: upiTxnId
      });

      const { status, message, amount, already_processed } = response.data;

      if (status === "success") {
        toast.success(already_processed ? "Payment was already processed" : `Wallet recharged! ₹${amount} added.`);
        closeRechargeDialog();
        fetchWalletData();
      } else if (status === "failed") {
        toast.error(message || "Payment failed");
        setUpiTxnId(null);
      } else {
        toast.info("Payment is still being processed. Please wait and check again.");
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to check payment status");
    } finally {
      setCheckingUpiStatus(false);
    }
  };

  const closeRechargeDialog = () => {
    setShowRechargeDialog(false);
    setRechargeAmount("");
    setPaymentMethod(null);
    setUpiTxnId(null);
  };

  const handleDownloadReceipt = async (txn) => {
    setDownloadingReceipt(txn.id);
    try {
      const res = await axios.get(
        `${API}/wallet/transaction/${txn.id}/receipt`,
        { responseType: "blob" }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", `receipt_${txn.id.substring(0, 8)}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Receipt downloaded");
    } catch (error) {
      console.error("Receipt download failed:", error);
      toast.error("Failed to download receipt");
    } finally {
      setDownloadingReceipt(null);
    }
  };

  if (loading) {
    return (
      <SellerLayout user={user} onLogout={onLogout}>
        <div className="flex justify-center py-12">
          <div className="text-slate-600">Loading...</div>
        </div>
      </SellerLayout>
    );
  }

  const isLowBalance = balance < 0;
  
  const enteredAmount = parseFloat(rechargeAmount) || 0;
  const razorpayFee = enteredAmount * 0.03;
  const razorpayTotal = enteredAmount + razorpayFee;

  return (
    <SellerLayout user={user} onLogout={onLogout}>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-800 mb-1 sm:mb-2">My Wallet</h1>
          <p className="text-sm sm:text-base text-slate-600">View your wallet balance and transaction history</p>
        </div>

        {isLowBalance && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800 font-semibold">Low Balance Alert</p>
            <p className="text-red-600 text-sm">Your wallet balance is negative. Please contact admin to recharge.</p>
          </div>
        )}

        {/* Current Balance Card */}
        <Card className="border-0 shadow-lg">
          <CardHeader>
            <CardTitle className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5" style={{color: '#00A699'}} />
                Current Balance
              </div>
              <Button 
                onClick={() => setShowRechargeDialog(true)}
                style={{backgroundColor: '#00A699'}}
                className="hover:opacity-90 w-full sm:w-auto"
              >
                <Plus className="w-4 h-4 mr-2" />
                Recharge Wallet
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center py-6 sm:py-8">
              <p className="text-sm text-slate-500 mb-2">Available Balance</p>
              <p className={`text-4xl sm:text-6xl font-bold break-all ${isLowBalance ? 'text-red-600' : 'text-green-600'}`}>
                ₹{balance.toLocaleString()}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Transaction History */}
        <Card className="border-0 shadow-lg">
          <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 space-y-0">
            <CardTitle>Transaction History</CardTitle>
            {transactions.length > 0 && (
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                className="flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                <Download className="w-4 h-4" />
                Export CSV
              </Button>
            )}
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Wallet className="w-16 h-16 mx-auto mb-4 text-slate-300" />
                <p>No transactions yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Date</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Type</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Amount</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Recharge Via</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Transaction ID</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Description</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Balance After</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Recharged By</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-slate-700 uppercase tracking-wider whitespace-nowrap">Receipt</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-200">
                    {transactions.map((txn) => (
                      <tr key={txn.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3 align-middle text-sm text-slate-700">
                          <div className="whitespace-nowrap">
                            <div>{formatToIST(txn.created_at, false)}</div>
                            <div className="text-xs text-slate-500">{formatToIST(txn.created_at).split(', ')[1]}</div>
                          </div>
                        </td>
                        <td className="px-4 py-3 align-middle whitespace-nowrap">
                          <Badge className={txn.type === 'credit' ? 'bg-green-100 text-green-800 border-0' : 'bg-red-100 text-red-800 border-0'}>
                            {txn.type === 'credit' ? (
                              <><TrendingUp className="w-3 h-3 inline mr-1" />Credit</>
                            ) : (
                              <><TrendingDown className="w-3 h-3 inline mr-1" />Debit</>
                            )}
                          </Badge>
                        </td>
                        <td className="px-4 py-3 align-middle whitespace-nowrap text-right">
                          <span className={`text-sm font-medium ${txn.type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                            {txn.type === 'credit' ? '+' : '-'}₹{(txn.amount || 0).toLocaleString()}
                          </span>
                        </td>
                        <td className="px-4 py-3 align-middle whitespace-nowrap text-sm text-slate-700">
                          {txn.recharge_via || '-'}
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-slate-700">
                          {txn.transaction_id || txn.razorpay_payment_id || '-'}
                        </td>
                        <td className="px-4 py-3 align-middle text-sm text-slate-700">
                          {txn.description || '-'}
                        </td>
                        <td className="px-4 py-3 align-middle whitespace-nowrap text-right text-sm font-medium text-slate-900">
                          {txn.balance_after ? `₹${txn.balance_after.toLocaleString()}` : '-'}
                        </td>
                        <td className="px-4 py-3 align-middle whitespace-nowrap text-sm text-slate-600">
                          {txn.recharged_by || 'System'}
                        </td>
                        <td className="px-4 py-3 align-middle text-center">
                          {txn.type === 'credit' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDownloadReceipt(txn)}
                              disabled={downloadingReceipt === txn.id}
                              data-testid={`download-receipt-${txn.id}`}
                              title="Download Receipt"
                            >
                              {downloadingReceipt === txn.id ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <FileDown className="w-4 h-4 text-teal-600" />
                              )}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Pagination */}
            {transactions.length > 0 && pagination.pages > 1 && (
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-3 px-4 py-3 border-t mt-4">
                <div className="text-xs sm:text-sm text-slate-600 text-center sm:text-left">
                  Showing {Math.min((currentPage - 1) * pagination.limit + 1, pagination.total)} to {Math.min(currentPage * pagination.limit, pagination.total)} of {pagination.total} transactions
                </div>
                <div className="flex gap-2 justify-center">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {[...Array(pagination.pages)].map((_, i) => {
                      const pageNum = i + 1;
                      // Show first, last, current, and neighbors
                      if (
                        pageNum === 1 ||
                        pageNum === pagination.pages ||
                        (pageNum >= currentPage - 1 && pageNum <= currentPage + 1)
                      ) {
                        return (
                          <Button
                            key={pageNum}
                            variant={currentPage === pageNum ? "default" : "outline"}
                            size="sm"
                            onClick={() => setCurrentPage(pageNum)}
                            style={currentPage === pageNum ? {backgroundColor: '#00A699'} : {}}
                          >
                            {pageNum}
                          </Button>
                        );
                      } else if (
                        pageNum === currentPage - 2 ||
                        pageNum === currentPage + 2
                      ) {
                        return <span key={pageNum} className="px-2">...</span>;
                      }
                      return null;
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === pagination.pages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Recharge Dialog */}
        <Dialog open={showRechargeDialog} onOpenChange={(open) => { if (!open) closeRechargeDialog(); else setShowRechargeDialog(true); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Recharge Wallet</DialogTitle>
              <DialogDescription>
                Enter the amount and choose your payment method.
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-2">
              {/* Amount Input */}
              <div>
                <Label htmlFor="amount">Amount (₹)</Label>
                <Input
                  id="amount"
                  type="number"
                  placeholder="Enter amount"
                  value={rechargeAmount}
                  onChange={(e) => { setRechargeAmount(e.target.value); setPaymentMethod(null); setUpiTxnId(null); }}
                  min="100"
                  step="1"
                  disabled={isProcessing || !!upiTxnId}
                  data-testid="recharge-amount-input"
                />
                <p className="text-xs text-slate-500 mt-1">Minimum: ₹100</p>
              </div>

              {/* Payment Method Selection */}
              {enteredAmount >= 100 && !upiTxnId && (
                <div className="space-y-3">
                  <Label>Select Payment Method</Label>
                  <div className={`grid ${SHOW_UPI_RECHARGE ? "grid-cols-2" : "grid-cols-1"} gap-3`}>
                    {/* UPI Option — temporarily hidden via SHOW_UPI_RECHARGE */}
                    {SHOW_UPI_RECHARGE && (
                    <button
                      onClick={() => setPaymentMethod("upi")}
                      disabled={isProcessing}
                      className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                        paymentMethod === "upi"
                          ? "border-teal-500 bg-teal-50"
                          : "border-slate-200 hover:border-slate-300 bg-white"
                      }`}
                      data-testid="payment-method-upi"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <Smartphone className="w-5 h-5 text-teal-600" />
                        <span className="font-semibold text-sm text-slate-800">UPI</span>
                      </div>
                      <p className="text-xs text-green-600 font-medium">0% Gateway Fee</p>
                      {paymentMethod === "upi" && (
                        <div className="absolute top-2 right-2 w-3 h-3 bg-teal-500 rounded-full"></div>
                      )}
                    </button>
                    )}

                    {/* Razorpay Option */}
                    <button
                      onClick={() => setPaymentMethod("razorpay")}
                      disabled={isProcessing}
                      className={`relative p-4 rounded-lg border-2 text-left transition-all ${
                        paymentMethod === "razorpay"
                          ? "border-blue-500 bg-blue-50"
                          : "border-slate-200 hover:border-slate-300 bg-white"
                      }`}
                      data-testid="payment-method-razorpay"
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <CreditCard className="w-5 h-5 text-blue-600" />
                        <span className="font-semibold text-sm text-slate-800">Razorpay</span>
                      </div>
                      <p className="text-xs text-amber-600 font-medium">3% Gateway Fee</p>
                      {paymentMethod === "razorpay" && (
                        <div className="absolute top-2 right-2 w-3 h-3 bg-blue-500 rounded-full"></div>
                      )}
                    </button>
                  </div>
                </div>
              )}

              {/* Fee Summary */}
              {enteredAmount >= 100 && paymentMethod && !upiTxnId && (
                <div className="bg-slate-50 p-4 rounded-lg space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Wallet Amount:</span>
                    <span className="font-semibold">₹{enteredAmount.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Gateway Fee ({paymentMethod === "upi" ? "0%" : "3%"}):</span>
                    <span className="font-semibold">₹{paymentMethod === "upi" ? "0.00" : razorpayFee.toFixed(2)}</span>
                  </div>
                  <div className="border-t pt-2 flex justify-between">
                    <span className="font-semibold">Total Amount:</span>
                    <span className="font-bold text-lg" style={{color: '#00A699'}}>
                      ₹{paymentMethod === "upi" ? enteredAmount.toFixed(2) : razorpayTotal.toFixed(2)}
                    </span>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">
                    ₹{enteredAmount.toFixed(2)} will be added to your wallet after successful payment
                  </p>
                </div>
              )}

              {/* UPI Payment Initiated - Check Status */}
              {upiTxnId && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 space-y-3">
                  <div className="flex items-center gap-2">
                    <ExternalLink className="w-4 h-4 text-amber-600" />
                    <span className="text-sm font-medium text-amber-800">UPI Payment page opened in new tab</span>
                  </div>
                  <p className="text-xs text-amber-700">Complete the payment in the new tab, then click below to verify.</p>
                  <Button
                    onClick={handleCheckUpiStatus}
                    disabled={checkingUpiStatus}
                    className="w-full"
                    style={{backgroundColor: '#00A699'}}
                  >
                    {checkingUpiStatus ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Checking Status...
                      </>
                    ) : (
                      "I have completed payment - Check Status"
                    )}
                  </Button>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={closeRechargeDialog}
                disabled={isProcessing}
              >
                Cancel
              </Button>
              {!upiTxnId && (
                <Button
                  onClick={paymentMethod === "upi" ? handleRechargeUPI : handleRechargeRazorpay}
                  disabled={isProcessing || !paymentMethod || enteredAmount < 100}
                  style={{backgroundColor: '#00A699'}}
                  className="hover:opacity-90"
                  data-testid="proceed-to-pay-btn"
                >
                  {isProcessing ? "Processing..." : "Proceed to Pay"}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </SellerLayout>
  );
};

export default SellerWalletPage;
